import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { getAppConfig } from '@nxsys/utils';
import { Form, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import { Moment } from 'moment';
import React from 'react';
import AppRateInput from '@nxsys/components/AppRateInput';

type NetPayFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: NetPayFilterValues) => void;
  isBureau?: boolean;
};

export type NetPayFilterValues = {
  paymentDateFrom: Moment;
  paymentDateTo: Moment;
  taxPeriodFrom: number;
  taxPeriodTo: number;
  companies: Option<string>[];
  agencies: Option<string>[];
  paymentModels: Option<string>[];
};

const NetPayFilterModal: React.FC<NetPayFilterModalProps> = ({
  isOpen,
  toggle,
  onFilter,
  isBureau,
}: NetPayFilterModalProps) => {
  const { messages } = useTypedIntl();
  const defaultPaymentModel = getAppConfig('paymentModels');

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const ableToClearFilter =
    watchValue && Object.keys(watchValue).some((key) => !!watchValue[key]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    onFilter(values);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item label={isBureau ? messages['common.employer'] : messages['common.paymentCompany']} name="companies">
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            mode="multiple"
            placeholder={messages['form.pleaseSelect']}
            onChangeValue={(value) => {
              form.setFieldsValue({
                companies: value,
              });
            }}
          />
        </Form.Item>

        {!isBureau && (
          <Form.Item label={messages['common.agency']} name="agencies">
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              mode="multiple"
              placeholder={messages['form.pleaseSelect']}
              onChangeValue={(value) => {
                form.setFieldsValue({
                  agencies: value,
                });
              }}
            />
          </Form.Item>
        )}

        <Form.Item label={messages['common.paymentModel']} name="paymentModels">
          <AppSelect
            options={defaultPaymentModel ?? []}
            mode="multiple"
            placeholder={messages['form.pleaseSelect']}
            onChangeValue={(value: Option<string>[]) => {
              form.setFieldsValue({
                paymentModels: value,
              });
            }}
          />
        </Form.Item>

        <Typography.Text>{messages['common.paymentDate']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="paymentDateFrom">
            <AppDatePicker
              placeholder={messages['common.fromDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  paymentDateFrom: date,
                });
              }}
              format="DD/MM/YYYY"
              maxDate={watchValue?.paymentDateTo}
            />
          </Form.Item>
          <Form.Item name="paymentDateTo">
            <AppDatePicker
              placeholder={messages['common.toDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  paymentDateTo: date,
                });
              }}
              format="DD/MM/YYYY"
              minDate={watchValue?.paymentDateFrom}
            />
          </Form.Item>
        </div>

        <Typography.Text>{messages['common.taxPeriod']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="taxPeriodFrom">
            <AppRateInput placeholder={messages['common.from']} />
          </Form.Item>
          <Form.Item name="taxPeriodTo">
            <AppRateInput placeholder={messages['common.to']} />
          </Form.Item>
        </div>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default NetPayFilterModal;
